import { useState } from 'react'

import { useMutation, useQuery, useQueryClient } from 'react-query'

import { TaxAPI } from '@webapp/api/TaxAPI'
import { toast } from '@webapp/components/Notification'
import { type TaxFormSchema } from '@webapp/components/TaxForm'
import { useProcurifyIntl } from '@webapp/hooks'
import { type Tax } from '@webapp/models'
import messages from './messages'

interface UseTaxSelectCreateOptions {
  onTaxCreated: (tax: Tax) => void
  onModalClose?: () => void
}

export const useTaxSelectCreate = ({
  onTaxCreated,
  onModalClose,
}: UseTaxSelectCreateOptions) => {
  const intl = useProcurifyIntl()
  const queryClient = useQueryClient()

  const [isTaxFormModalOpen, setIsTaxFormModalOpen] = useState(false)

  const { data: taxOptions, isLoading: isTaxOptionsLoading } = useQuery({
    queryKey: 'active-tax-options',
    queryFn: async (params) => {
      const res = await TaxAPI.getActiveTaxes({
        page_size: 999,
        ...params,
      })
      return res.data
    },
    enabled: true,
    onError: () => {
      toast.error({
        title: intl.formatMessage(messages.getTaxesErrorTitle),
        children: intl.formatMessage(messages.getTaxesErrorMessage),
      })
    },
  })

  const createTaxMutation = useMutation({
    mutationFn: async (taxFormData: TaxFormSchema) => {
      const res = await TaxAPI.createTax({
        name: taxFormData.name,
        percentage: (parseFloat(taxFormData.percentage) / 100).toFixed(6),
        description: taxFormData.description,
        is_inclusive: taxFormData.isInclusive,
      })
      return res.data
    },
    onSuccess: (newTax: Tax) => {
      onTaxCreated(newTax)
      queryClient.invalidateQueries('active-tax-options')
      setIsTaxFormModalOpen(false)
      onModalClose?.()
    },
    onError: () => {
      toast.error({
        title: intl.formatMessage(messages.taxCreateErrorTitle),
        children: intl.formatMessage(messages.taxCreateErrorMessage),
      })
    },
  })

  return {
    createTax: createTaxMutation.mutate,
    taxOptions,
    isTaxOptionsLoading,
    isTaxFormModalOpen,
    setIsTaxFormModalOpen,
  }
}
